import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, type Location } from 'react-router-dom';
import api from '../services/api';
import type { InspectionOrderDevice } from '../types/inspectionOrder';
import {
  normalizeInspectionOrderDevice,
  type RawInspectionOrderDevice
} from '../utils/normalizeInspectionOrder';
import type { DeviceReportTemplate } from '../types/reportTemplate';
import { normalizeDeviceReportTemplate } from '../utils/normalizeDeviceReportTemplate';
import { OrderDeviceDetailsView } from '../components/orders/OrderDeviceDetailsView';
import './OrderDeviceDetailsPage.css';

type DeviceDetailsState = {
  isLoading: boolean;
  error: string | null;
  device: InspectionOrderDevice | null;
  orderReference: string | null;
};

type DeviceTemplateState = {
  template: DeviceReportTemplate | null;
  isLoading: boolean;
  error: string | null;
};

type DeviceDetailsLocationState = {
  from?: string;
  deviceLookup?: {
    id: string | null;
    identifier: string | null;
    index?: number;
  };
};

const DEVICE_NOT_FOUND_MESSAGE = 'Gerät wurde nicht gefunden.';
const DEVICE_LOAD_ERROR_MESSAGE = 'Gerät konnte nicht geladen werden. Bitte erneut versuchen.';

const isNonEmptyString = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export const OrderDeviceDetailsPage = () => {
  const { orderId, deviceId } = useParams();
  const location = useLocation() as Location & { state?: DeviceDetailsLocationState };
  const navigate = useNavigate();
  const [state, setState] = useState<DeviceDetailsState>({
    isLoading: true,
    error: null,
    device: null,
    orderReference: null
  });
  const [templateState, setTemplateState] = useState<DeviceTemplateState>({
    template: null,
    isLoading: false,
    error: null
  });
  const [templateReloadIndex, setTemplateReloadIndex] = useState(0);

  const locationState = location.state ?? {};
  const decodedDeviceId = useMemo(() => (deviceId ? decodeURIComponent(deviceId) : null), [deviceId]);

  useEffect(() => {
    let cancelled = false;

    const loadDevice = async () => {
      if (!orderId || !decodedDeviceId) {
        if (!cancelled) {
          setState({
            isLoading: false,
            error: DEVICE_LOAD_ERROR_MESSAGE,
            device: null,
            orderReference: orderId ?? null
          });
          setTemplateState({
            template: null,
            isLoading: false,
            error: null
          });
        }
        return;
      }

      if (!cancelled) {
        setState((previous) => ({
          ...previous,
          isLoading: true,
          error: null,
          device: null,
          orderReference: null
        }));
        setTemplateState({
          template: null,
          isLoading: true,
          error: null
        });
      }

      try {
        const directResponse = await api.get<RawInspectionOrderDevice>(
          `technicians/me/inspection-orders/${orderId}/devices/${decodedDeviceId}`
        );
        const directDevice = ((directResponse as any)?.data ?? directResponse) as RawInspectionOrderDevice;
        const normalizedDirect = normalizeInspectionOrderDevice(directDevice);
        
        if (!normalizedDirect) {
          throw new Error('device-not-found');
        }

        if (!cancelled) {
          setState({
            isLoading: false,
            error: null,
            device: normalizedDirect,
            orderReference: orderId
          });
        }
      } catch (error) {
        console.error('orderDeviceDetails:load', error);
        if (!cancelled) {
          setState({
            isLoading: false,
            error: error instanceof Error && error.message === 'device-not-found'
              ? DEVICE_NOT_FOUND_MESSAGE
              : DEVICE_LOAD_ERROR_MESSAGE,
            device: null,
            orderReference: orderId ?? null
          });
          setTemplateState({
            template: null,
            isLoading: false,
            error: null
          });
        }
      }
    };

    void loadDevice();

    return () => {
      cancelled = true;
    };
  }, [decodedDeviceId, orderId]);

  useEffect(() => {
    const deviceUuid = state.device?.id;
    if (!orderId || !deviceUuid) {
      setTemplateState({
        template: null,
        isLoading: false,
        error: null
      });
      return;
    }

    let cancelled = false;
    setTemplateState((previous) => ({
      template: previous.template && previous.template.deviceId === deviceUuid ? previous.template : null,
      isLoading: true,
      error: null
    }));

    const loadTemplate = async () => {
      try {
        const response = await api.get<DeviceReportTemplate>(
          `technicians/me/inspection-orders/${orderId}/devices/${deviceUuid}/report-template`
        );
        const normalized = normalizeDeviceReportTemplate(response as unknown as DeviceReportTemplate);

        if (!cancelled) {
          setTemplateState({
            template: normalized,
            isLoading: false,
            error: null
          });
        }
      } catch (templateError) {
        console.error('orderDeviceDetails:template', templateError);
        if (!cancelled) {
          setTemplateState({
            template: null,
            isLoading: false,
            error: 'Prüfberichtsvorlage konnte nicht geladen werden.'
          });
        }
      }
    };

    void loadTemplate();

    return () => {
      cancelled = true;
    };
  }, [orderId, state.device?.id, templateReloadIndex]);

  const handleBack = useCallback(() => {
    const from = locationState.from;
    if (from) {
      navigate(from, { replace: true });
      return;
    }

    if (orderId) {
      navigate(`/inspections/${orderId}?tab=devices`, { replace: true });
    } else {
      navigate('/inspections', { replace: true });
    }
  }, [locationState.from, navigate, orderId]);

  const handleReloadTemplate = useCallback(() => {
    setTemplateReloadIndex((previous) => previous + 1);
  }, []);

  const { isLoading, error, device, orderReference } = state;

  return (
    <section className="order-device-details-page">
      <header className="order-device-details-page__header">
        <h2>Gerätedetails</h2>
        {orderReference && (
          <p className="order-device-details-page__meta">
            Auftrag: <strong>{orderReference}</strong>
          </p>
        )}
      </header>

      {isLoading && (
        <div className="order-device-details-page__state order-device-details-page__state--loading" role="status">
          <span className="order-device-details-page__spinner" aria-hidden="true" />
          <span>Gerät wird geladen ...</span>
        </div>
      )}

      {!isLoading && error && (
        <div className="order-device-details-page__state order-device-details-page__state--error" role="alert">
          <p>{error}</p>
          <button type="button" className="order-device-details-page__back-inline" onClick={handleBack}>
            Zurück
          </button>
        </div>
      )}

      {!isLoading && !error && device && orderId && (
        <OrderDeviceDetailsView
          device={device}
          orderId={orderId}
          onBack={handleBack}
          template={templateState.template}
          isTemplateLoading={templateState.isLoading}
          templateError={templateState.error}
          onReloadTemplate={handleReloadTemplate}
        />
      )}
    </section>
  );
};
